
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ReportModel } from '@/models/report';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { ReportBuilderEventHandler } from '@/modules/reportBuilderEvents';
import SampleDataBadge from '@/components/SampleDataBadge.vue';
import dateFormat from 'dateformat';
import { convertTimeRange } from '@/services/datetime_convertor';

@Component({
  components: {
    SampleDataBadge
  }
})
export default class ReportBuilderPreview extends Vue {
  // Watermark positioning configuration
  private static readonly WATERMARK_POSITIONS = [
    { top: '20%', left: '10%' },
    { top: '20%', left: '60%' },
    { top: '50%', left: '-10%' },
    { top: '50%', left: '35%' },
    { top: '50%', left: '85%' },
    { top: '80%', left: '15%' }
  ];

  @Prop({ required: true }) parameters!: any;
  @Prop({ default: 0 }) confidenceScore!: number;
  @Prop({ default: false }) savingReport!: boolean;
  @Prop({ required: false }) eventHandler?: ReportBuilderEventHandler;
  @Prop({ default: false }) isAthenaReady!: boolean;
  @Prop({ required: false }) aiResponse?: any;  // Full AI response for sample data detection

  private loading: boolean = false;
  private reportModel: ReportModel = new ReportModel();
  private headers: any[] = [];
  private rows: any[] = [];
  private totalRows: number = 0;
  private hasRetried: boolean = false;
  private dataNotReadyHeaders: any[] = [];
  
  get hasData(): boolean {
    return this.rows.length > 0;
  }
  
  get isDataReady(): boolean {
    // Sample data is always ready (onboarding mode)
    if (this.isSampleData && this.hasSampleData) {
      return true;
    }
    // Real data is ready only when both athena checks pass
    return this.isAthenaReady;
  }
  
  get confidencePercentage(): number {
    return Math.round(this.confidenceScore * 100);
  }
  
  get confidenceColor(): string {
    if (this.confidenceScore >= 0.8) return 'success';
    if (this.confidenceScore >= 0.5) return 'warning';
    return 'error';
  }

  // Sample data detection (onboarding mode)
  get isSampleData(): boolean {
    return this.aiResponse?.is_sample_data === true;
  }

  get hasSampleData(): boolean {
    return this.isSampleData &&
           Array.isArray(this.aiResponse?.sample_data) &&
           this.aiResponse.sample_data.length > 0;
  }

  get formattedHeaders(): any[] {
    // Get selected column codes in the order AI suggested
    const selectedColumnCodes = this.getSelectedColumnCodes();
    
    // Create a map of all headers by code for quick lookup
    const headerMap = new Map(this.headers.map(h => [h.code, h]));
    
    // Build headers array in the exact order of AI selection
    const orderedHeaders = selectedColumnCodes
      .map(code => headerMap.get(code))
      .filter(col => col !== undefined); // Filter out any codes that don't exist
    
    return orderedHeaders.map(col => ({
      text: col.name,
      value: col.code,
      dataType: col.dataType,
      align: this.getAlignment(col.dataType),
      sortable: false, // Disable sorting in preview
      class: this.getHeaderClass(col.dataType)
    }));
  }
  
  get formattedRows(): any[] {
    // For sample data (onboarding mode), use this.rows directly
    // For real data, use ReportModel's built-in row formatting
    const formattedData = (this.isSampleData && this.hasSampleData)
      ? this.rows
      : this.reportModel.getReportRowsData();

    if (this.isSampleData) {
      console.log('[ReportBuilderPreview] formattedRows using sample data, rows:', formattedData.length);
    }

    // Get selected column codes in the order AI suggested
    const selectedColumnCodes = this.getSelectedColumnCodes();

    // Create a map of all headers by code for quick lookup
    const headerMap = new Map(this.headers.map(h => [h.code, h]));

    // Additional formatting for display - process columns in AI order
    return formattedData.map(row => {
      const formattedRow: any = {};
      
      // Process columns in the exact order AI suggested
      for (const colCode of selectedColumnCodes) {
        const col = headerMap.get(colCode);
        if (!col) continue; // Skip if column not found
        
        const value = row[col.code];
        
        // Apply formatting based on dataType
        if (col.dataType === 'currency' && value !== '-') {
          // Check if value already contains currency formatting (e.g., "$255,110.00")
          if (typeof value === 'string' && value.includes('$')) {
            formattedRow[col.code] = value; // Already formatted, use as-is
          } else {
            formattedRow[col.code] = CurrencyHelper.formatCurrency(value);
          }
        } else if (col.dataType === 'percent' && value !== '-') {
          formattedRow[col.code] = this.formatPercent(value);
        } else if ((col.dataType === 'number' || col.dataType === 'decimal') && value !== '-') {
          formattedRow[col.code] = this.formatNumber(value);
        } else if (col.dataType === 'date' && value !== '-') {
          formattedRow[col.code] = this.formatDate(value);
        } else {
          formattedRow[col.code] = value;
        }
      }
      
      return formattedRow;
    });
  }
  
  @Watch('parameters', { deep: true })
  async onParametersChange() {
    await this.loadPreviewData();
  }
  
  async mounted() {
    if (this.parameters) {
      await this.loadPreviewData();
      
      // Track preview viewed
      if (this.eventHandler) {
        this.eventHandler.trackReportPreviewInteraction('viewed', {
          rowCount: this.rows.length,
          columnCount: this.headers.length
        });
      }
    }
  }
  
  private async loadPreviewData(useCache: boolean = true) {
    if (!this.parameters?.measure || !this.parameters?.dimension) {
      return;
    }

    // ONBOARDING MODE: Use sample data directly without API call
    if (this.isSampleData && this.hasSampleData) {
      console.log('[ReportBuilderPreview] Sample data detected, using directly without API call');
      this.loadSampleDataPreview();
      return;
    }

    // Check if data is ready
    if (!this.isDataReady) {
      // Just set up headers without fetching data
      await this.loadHeadersOnly();
      return;
    }

    this.loading = true;
    
    try {
      // Configure report model with AI parameters
      this.reportModel = new ReportModel();
      this.reportModel.typeReport = 'TABLE';
      this.reportModel.measure = { code: this.parameters.measure } as any;
      this.reportModel.dimension = { code: this.parameters.dimension } as any;
      this.reportModel.page = 1;
      this.reportModel.isDetail = true;
      this.reportModel.analyticsType = { code: 'Top High' } as any;
      // Use AI-suggested limit if provided, otherwise default to 10
      this.reportModel.perPage = this.parameters.limit || 10;
      
      // Set time range
      if (this.parameters.time_range) {
        // Check if time_range has a period that needs conversion
        if (this.parameters.time_range.period) {
          // Convert period string to from/to dates
          const convertedRange = convertTimeRange(this.parameters.time_range.period);
          this.reportModel.filterTimerange = [
            convertedRange.start,
            convertedRange.end
          ];
        } else if (this.parameters.time_range.from && this.parameters.time_range.to) {
          // Use provided from/to dates directly
          this.reportModel.filterTimerange = [
            this.parameters.time_range.from,
            this.parameters.time_range.to
          ];
        }
      }
      
      // Set columns - handle both array of objects and array of strings
      if (this.parameters.columns?.length > 0) {
        // If columns are objects with 'code' property, extract the codes
        if (typeof this.parameters.columns[0] === 'object' && this.parameters.columns[0].code) {
          this.reportModel.reportColumns = this.parameters.columns;
        } else if (typeof this.parameters.columns[0] === 'string') {
          // If columns are strings, wrap them in objects
          this.reportModel.reportColumns = this.parameters.columns.map((col: string) => ({ code: col }));
        }
      }
      
      // Set sort
      if (this.parameters.sort) {
        this.reportModel.sortBy = [this.parameters.sort.column];
        this.reportModel.sortDesc = [this.parameters.sort.direction === 'desc'];
      }
      
      // Set filters
      if (this.parameters.filters?.length > 0) {
        console.log('AI Parameters filters:', this.parameters.filters);
        // Convert filters to FilterColumnList format
        const filterColumns = this.convertFilters(this.parameters.filters);
        console.log('Converted filterColumns:', filterColumns);
        this.reportModel.filterColumns = filterColumns;
      }
      
      // Fetch data - pass cache parameter to getReport
      await this.reportModel.getReport(useCache);
      
      this.headers = this.reportModel.columns || [];
      this.rows = this.reportModel.rows || [];
      
      // Check if we got no data or error response
      if ((!this.rows || this.rows.length === 0) && (this.parameters.filters?.length > 0 || this.parameters.sort)) {
        console.warn('No data returned with filters/sort, retrying without them...');
        
        // Retry without filters and sort
        this.reportModel.filterColumns = undefined;
        this.reportModel.sortBy = [];
        this.reportModel.sortDesc = [];
        
        // Keep only the time range filter
        if (this.parameters.time_range) {
          this.reportModel.filterTimerange = [
            this.parameters.time_range.from,
            this.parameters.time_range.to
          ];
        }
        
        // Try fetching again without filters and sort
        await this.reportModel.getReport(useCache);
        
        this.headers = this.reportModel.columns || [];
        this.rows = this.reportModel.rows || [];
        
        // Notify user that filters/sort were removed
        if (this.rows && this.rows.length > 0) {
          console.info('Successfully loaded data after removing filters/sort');
          this.$emit('warning', 'Preview loaded without some filters or sorting due to data compatibility issues');
        }
      }
      
      // When we have a limit, we're showing exactly that many rows (or less if fewer available)
      // The total should reflect what we're actually showing in the preview
      if (this.parameters.limit) {
        this.totalRows = Math.min(this.parameters.limit, this.rows.length);
      } else {
        this.totalRows = this.rows.length;
      }
      
    } catch (error: any) {
      console.error('Failed to load preview data:', error);
      
      // If error is due to filters or sort, retry without them
      if ((this.parameters.filters?.length > 0 || this.parameters.sort) && !this.hasRetried) {
        this.hasRetried = true;
        console.warn('Error loading with filters/sort, retrying without them...');
        
        try {
          // Create a new report model without filters and sort
          this.reportModel = new ReportModel();
          this.reportModel.typeReport = 'TABLE';
          this.reportModel.measure = { code: this.parameters.measure } as any;
          this.reportModel.dimension = { code: this.parameters.dimension } as any;
          this.reportModel.page = 1;
          this.reportModel.isDetail = true;
          this.reportModel.perPage = this.parameters.limit || 10;
          
          // Keep time range
          if (this.parameters.time_range) {
            this.reportModel.filterTimerange = [
              this.parameters.time_range.from,
              this.parameters.time_range.to
            ];
          }
          
          // Keep columns
          if (this.parameters.columns?.length > 0) {
            if (typeof this.parameters.columns[0] === 'object' && this.parameters.columns[0].code) {
              this.reportModel.reportColumns = this.parameters.columns;
            } else if (typeof this.parameters.columns[0] === 'string') {
              this.reportModel.reportColumns = this.parameters.columns.map((col: string) => ({ code: col }));
            }
          }
          
          // Fetch without filters and sort
          await this.reportModel.getReport(useCache);
          
          this.headers = this.reportModel.columns || [];
          this.rows = this.reportModel.rows || [];
          
          if (this.parameters.limit) {
            this.totalRows = Math.min(this.parameters.limit, this.rows.length);
          } else {
            this.totalRows = this.rows.length;
          }
          
          if (this.rows && this.rows.length > 0) {
            console.info('Successfully loaded data after removing filters/sort');
            this.$emit('warning', 'Preview loaded without some filters or sorting due to compatibility issues');
          } else {
            this.$emit('error', 'Unable to load preview data');
            
            // Track error
            if (this.eventHandler) {
              this.eventHandler.trackReportPreviewInteraction('error', {
                error: 'No data after removing filters/sort'
              });
            }
          }
        } catch (retryError) {
          console.error('Failed to load even without filters/sort:', retryError);
          this.$emit('error', 'Unable to load preview data');
          
          // Track error
          if (this.eventHandler) {
            this.eventHandler.trackReportPreviewInteraction('error', {
              error: 'Failed retry without filters'
            });
          }
          
          this.headers = [];
          this.rows = [];
          this.totalRows = 0;
        }
      } else {
        this.$emit('error', 'Unable to load preview data');
        
        // Track error
        if (this.eventHandler) {
          this.eventHandler.trackReportPreviewInteraction('error', {
            error: 'Unable to load preview data'
          });
        }
        
        this.headers = [];
        this.rows = [];
        this.totalRows = 0;
      }
    } finally {
      this.loading = false;
      this.hasRetried = false; // Reset for next load
    }
  }
  
  private async refreshPreview() {
    // Track refresh action
    if (this.eventHandler) {
      this.eventHandler.trackReportPreviewInteraction('refreshed');
    }
    
    // Refresh without cache to get fresh data
    await this.loadPreviewData(false);
  }
  
  private getAlignment(dataType: string): string {
    switch(dataType) {
      case 'number':
      case 'decimal':
      case 'currency':
      case 'percent':
        return 'end';
      case 'date':
        return 'center';
      default:
        return 'start';
    }
  }
  
  private getHeaderClass(dataType: string): string {
    if (['number', 'decimal', 'currency', 'percent'].includes(dataType)) {
      return 'text-right';
    }
    return '';
  }
  
  private getCellClass(header: any): string {
    const classes = [];
    if (header.align === 'end') classes.push('text-right');
    if (header.align === 'center') classes.push('text-center');
    return classes.join(' ');
  }
  
  private formatNumber(value: any): string {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('en-US');
  }
  
  private formatPercent(value: any): string {
    const num = parseFloat(value) || 0;
    return `${(num).toFixed(2)}%`;
  }
  
  private formatDate(value: any): string {
    try {
      return dateFormat(value, 'mmm d, yyyy', true);
    } catch {
      return value || '-';
    }
  }
  
  private convertFilters(filters: any[]): any {
    // Convert AI filter format to FilterColumnList format
    // The API returns filters with 'code' and 'name' properties directly
    const filterColumns = {
      items: filters.map(f => ({
        code: f.code || f.column,  // Use code first (from API), fallback to column
        name: f.name || f.column,   // Use name from API
        operator: f.operator,
        value: Array.isArray(f.value) ? f.value : [f.value],
        dataType: f.dataType || 'string'
      }))
    };
    return filterColumns;
  }
  
  private getSelectedColumnCodes(): string[] {
    // Extract column codes from AI parameters
    if (!this.parameters?.columns || this.parameters.columns.length === 0) {
      // If no columns specified, return all column codes as fallback
      return this.headers.map(h => h.code);
    }
    
    // Handle both array of objects with 'code' property and array of strings
    if (typeof this.parameters.columns[0] === 'object' && this.parameters.columns[0].code) {
      return this.parameters.columns.map((col: any) => col.code);
    } else if (typeof this.parameters.columns[0] === 'string') {
      return this.parameters.columns;
    }
    
    // Default fallback
    return this.headers.map(h => h.code);
  }

  /**
   * Load sample data for preview without making API calls (onboarding mode)
   * Uses sample_data array directly from AI response
   */
  private loadSampleDataPreview() {
    console.log('[ReportBuilderPreview] Loading sample data preview for onboarding mode');
    console.log('[ReportBuilderPreview] aiResponse:', this.aiResponse);
    console.log('[ReportBuilderPreview] sample_data:', this.aiResponse?.sample_data);
    console.log('[ReportBuilderPreview] is_sample_data:', this.aiResponse?.is_sample_data);

    // Set loading to true first
    this.loading = true;

    try {
      let normalizedColumns: any[];

      // Guard: Check if parameters.columns exists and is an array
      if (Array.isArray(this.parameters?.columns)) {
        // Normalize columns - convert string entries to objects to support both formats
        normalizedColumns = this.parameters.columns.map((col: any) => {
          if (typeof col === 'string') {
            return { code: col, name: col };
          }
          return col;
        });
      } else if (Array.isArray(this.aiResponse?.sample_data) && this.aiResponse.sample_data.length > 0) {
        // Derive columns from the first row of sample_data
        const firstRow = this.aiResponse.sample_data[0];
        if (Array.isArray(firstRow)) {
          console.log('[ReportBuilderPreview] Deriving columns from sample_data first row');
          normalizedColumns = firstRow.map((_, index: number) => ({
            code: `col${index}`,
            name: `col${index}`
          }));
        } else {
          // sample_data exists but first row is not an array - bail out
          console.warn('[ReportBuilderPreview] sample_data first row is not an array, cannot derive columns');
          this.headers = [];
          this.rows = [];
          this.totalRows = 0;
          this.loading = false;
          return;
        }
      } else {
        // Neither parameters.columns nor valid sample_data exists - bail out gracefully
        console.warn('[ReportBuilderPreview] No columns available in parameters.columns or sample_data');
        this.headers = [];
        this.rows = [];
        this.totalRows = 0;
        this.loading = false;
        return;
      }

      // Map normalized columns to headers format with human-readable names
      const headers = normalizedColumns.map((col: any) => ({
        code: col.code,
        name: this.formatColumnName(col.name || col.code),  // Format to Title Case with spaces
        dataType: col.dataType || 'string'
      }));

      // Convert sample_data array of arrays to row objects
      // Each array in sample_data corresponds to a row, values match column order
      const rows = this.aiResponse.sample_data.map((rowArray: any[]) => {
        const rowObject: any = {};
        headers.forEach((header: any, index: number) => {
          rowObject[header.code] = rowArray[index];
        });
        return rowObject;
      });

      // Update component data (Vue will track these changes)
      this.headers = headers;
      this.rows = rows;
      this.totalRows = rows.length;

      console.log('[ReportBuilderPreview] Sample data loaded successfully:', {
        columns: this.headers.length,
        rows: this.rows.length,
        sampleRow: this.rows[0],
        hasData: this.hasData,
        isDataReady: this.isDataReady
      });
    } catch (error) {
      console.error('[ReportBuilderPreview] Error loading sample data:', error);
      this.rows = [];
      this.headers = [];
    } finally {
      // Important: Set loading to false so the UI updates
      this.loading = false;
    }
  }

  private async loadHeadersOnly() {
    // Generate mock headers based on the measure and dimension
    this.loading = true;
    
    try {
      // Create basic headers structure
      const headers = [];
      
      // Add dimension column
      if (this.parameters.dimension) {
        const dimensionName = this.formatDimensionName(this.parameters.dimension);
        headers.push({
          code: this.parameters.dimension,
          name: dimensionName,
          dataType: 'string'
        });
      }
      
      // Add measure columns based on the selected columns or defaults
      if (this.parameters.columns?.length > 0) {
        for (const col of this.parameters.columns) {
          if (typeof col === 'string') {
            // If it's just a string, format it
            headers.push({
              code: col,
              name: this.formatColumnName(col),
              dataType: this.inferDataType(col)
            });
          } else {
            // If it's an object with code and name, format the name for display
            headers.push({
              code: col.code,
              name: this.formatColumnName(col.name || col.code),
              dataType: col.dataType || this.inferDataType(col.code)
            });
          }
        }
      } else {
        // Add default columns based on measure type
        const defaultColumns = this.getDefaultColumnsForMeasure(this.parameters.measure);
        headers.push(...defaultColumns);
      }
      
      this.dataNotReadyHeaders = headers;
      this.headers = headers;
      this.rows = []; // No data rows when data is not ready
      this.totalRows = 0;
      
    } finally {
      this.loading = false;
    }
  }
  
  private formatDimensionName(dimension: string): string {
    // Convert dimension code to readable name
    const dimensionMap: Record<string, string> = {
      'by_product': 'Product',
      'by_variant': 'Product Variant',
      'by_day': 'Day',
      'by_week': 'Week',
      'by_month': 'Month',
      'by_quarter': 'Quarter',
      'by_year': 'Year',
      'by_customer': 'Customer',
      'by_order': 'Order',
      'by_category': 'Category',
      'by_vendor': 'Vendor',
      'by_location': 'Location'
    };
    return dimensionMap[dimension] || dimension.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  
  private formatColumnName(column: string): string {
    // Convert column code to readable name
    return column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  
  private inferDataType(column: string): string {
    // Infer data type based on column name
    if (column.includes('price') || column.includes('revenue') || column.includes('cost') || column.includes('amount')) {
      return 'currency';
    }
    if (column.includes('quantity') || column.includes('count') || column.includes('units')) {
      return 'number';
    }
    if (column.includes('date') || column.includes('time')) {
      return 'date';
    }
    if (column.includes('percent') || column.includes('rate')) {
      return 'percent';
    }
    return 'string';
  }

  private getWatermarkPosition(index: number): Record<string, string> {
    return ReportBuilderPreview.WATERMARK_POSITIONS[index - 1] || {};
  }

  private getDefaultColumnsForMeasure(measure: string): any[] {
    // Return default columns based on measure type
    const measureColumns: Record<string, any[]> = {
      'sales_overview': [
        { code: 'revenue', name: 'Revenue', dataType: 'currency' },
        { code: 'units_sold', name: 'Units Sold', dataType: 'number' },
        { code: 'orders', name: 'Orders', dataType: 'number' }
      ],
      'actual_inventory_quantity': [
        { code: 'quantity_on_hand', name: 'Quantity on Hand', dataType: 'number' },
        { code: 'quantity_available', name: 'Quantity Available', dataType: 'number' }
      ],
      'inventory_forecasting': [
        { code: 'predicted_quantity', name: 'Predicted Quantity', dataType: 'number' },
        { code: 'days_of_stock', name: 'Days of Stock', dataType: 'number' }
      ],
      'inventory_replenishment': [
        { code: 'reorder_quantity', name: 'Reorder Quantity', dataType: 'number' },
        { code: 'reorder_point', name: 'Reorder Point', dataType: 'number' }
      ],
      'lost_revenue': [
        { code: 'lost_revenue', name: 'Lost Revenue', dataType: 'currency' },
        { code: 'out_of_stock_days', name: 'Out of Stock Days', dataType: 'number' }
      ]
    };
    
    return measureColumns[measure] || [
      { code: 'value', name: 'Value', dataType: 'number' }
    ];
  }
}
